import { ReactElement, useEffect, useState } from 'react'
import {
  Box,
  Card,
  Skeleton,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import { BarChart } from '@mui/x-charts/BarChart'
import { LineChart } from '@mui/x-charts/LineChart'

import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  fetchNurseGrowth,
  selectNurseGrowth,
  selectNurseGrowthStatus,
} from 'src/v3/content-pipeline/performance.slice'
import { ChartGuide } from 'src/v3/components/charts/chart-guide'

type Granularity = 'year' | 'month' | 'week'

let MONTH_ABBR = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
]

export let NurseGrowthBanner = (): ReactElement => {
  let dispatch = useAppDispatch()
  let nurseGrowth = useAppSelector(selectNurseGrowth)
  let status = useAppSelector(selectNurseGrowthStatus)
  let [granularity, setGranularity] = useState<Granularity>('year')

  useEffect(() => {
    if (status === 'uninitialized') {
      dispatch(fetchNurseGrowth())
    }
  }, [status, dispatch])

  if (status === 'pending' || status === 'uninitialized') {
    return (
      <Card sx={{ p: 2.5, mb: 3 }}>
        <Skeleton variant="text" width={220} height={28} sx={{ mb: 1 }} />
        <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 1 }} />
      </Card>
    )
  }

  if (status === 'rejected' || !nurseGrowth) {
    return <></>
  }

  let latestYear = nurseGrowth.byYear.at(-1)
  let prevYear = nurseGrowth.byYear.at(-2)
  let yoyDelta =
    latestYear && prevYear && prevYear.uniqueUsers > 0
      ? Math.round(
          ((latestYear.uniqueUsers - prevYear.uniqueUsers) /
            prevYear.uniqueUsers) *
            100,
        )
      : null

  let chartByYear = () => {
    let labels = nurseGrowth!.byYear.map((y) => y.year)
    let data = nurseGrowth!.byYear.map((y) => y.uniqueUsers)
    return (
      <BarChart
        height={140}
        series={[{ data, label: 'Unique Nurse Learners', color: '#7c4dff' }]}
        xAxis={[
          {
            data: labels,
            scaleType: 'band',
            valueFormatter: (v: number) => String(v),
          },
        ]}
        yAxis={[{ tickMinStep: 1 }]}
        margin={{ top: 10, right: 16, bottom: 30, left: 50 }}
      />
    )
  }

  let chartByMonth = () => {
    let labels = nurseGrowth!.byMonth.map(
      (m) => `${MONTH_ABBR[m.month - 1]} ${m.year}`,
    )
    let data = nurseGrowth!.byMonth.map((m) => m.uniqueUsers)
    return (
      <LineChart
        height={140}
        series={[
          {
            data,
            label: 'Unique Nurse Learners',
            color: '#7c4dff',
            showMark: false,
          },
        ]}
        xAxis={[{ data: labels, scaleType: 'band' }]}
        yAxis={[{ tickMinStep: 1 }]}
        margin={{ top: 10, right: 16, bottom: 30, left: 50 }}
      />
    )
  }

  let chartByWeek = () => {
    let labels = nurseGrowth!.byWeek.map((w) => w.isoWeek)
    let data = nurseGrowth!.byWeek.map((w) => w.uniqueUsers)
    return (
      <LineChart
        height={140}
        series={[
          {
            data,
            label: 'Unique Nurse Learners',
            color: '#7c4dff',
            showMark: false,
          },
        ]}
        xAxis={[{ data: labels, scaleType: 'band' }]}
        yAxis={[{ tickMinStep: 1 }]}
        margin={{ top: 10, right: 16, bottom: 30, left: 50 }}
      />
    )
  }

  return (
    <Card sx={{ p: 2.5, mb: 3 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'baseline',
          gap: 2,
          flexWrap: 'wrap',
          mb: 1.5,
        }}
      >
        <Typography variant="h6" component="h3">
          Nurse Learners
        </Typography>
        {granularity === 'year' && yoyDelta !== null && (
          <Typography
            variant="body2"
            sx={{
              color: yoyDelta >= 0 ? 'success.main' : 'error.main',
              fontWeight: 600,
            }}
          >
            {yoyDelta >= 0 ? '+' : ''}
            {yoyDelta}% vs prior year
          </Typography>
        )}
        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
          <ToggleButtonGroup
            value={granularity}
            exclusive
            size="small"
            aria-label="Time granularity"
            onChange={(_, v: Granularity | null) => {
              if (v) setGranularity(v)
            }}
          >
            <ToggleButton value="year">Year</ToggleButton>
            <ToggleButton value="month">Month</ToggleButton>
            <ToggleButton value="week">Week</ToggleButton>
          </ToggleButtonGroup>
          <ChartGuide
            methodology="Counts distinct users who earned at least one certificate (event or course) within each time bucket. A user who completed multiple courses in the same bucket counts as one. Only users who hold a Registered Nurse license at any rank are included."
            organization="Calendar buckets — years are Jan 1–Dec 31, months are the full calendar month, weeks are ISO weeks (Mon–Sun). The current period is always incomplete and will appear lower than finished periods."
            usage="Toggle Year / Month / Week to change time resolution. Year is best for long-run growth direction. Month reveals seasonality and campaign effects. Week is the highest resolution — expect normal week-to-week variance; look for sustained multi-week changes rather than single-week spikes."
          />
        </Box>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Distinct nurse users who completed at least one event or course, across
        all content.
      </Typography>
      {granularity === 'year' && chartByYear()}
      {granularity === 'month' && chartByMonth()}
      {granularity === 'week' && chartByWeek()}
    </Card>
  )
}
